import { useState } from 'react';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { Check, Copy } from '@phosphor-icons/react';
import { cn } from 'src/utils/className';
import { IconButton } from './IconButton';

interface CodeBlockProps {
  text: string;
  language: string;
  showLineNumbers?: boolean;
  wrapLongLines?: boolean;
  customStyle?: React.CSSProperties;
  showCopyButton?: boolean;
}

export const CodeBlock = ({
  text,
  language,
  showLineNumbers = false,
  wrapLongLines = false,
  customStyle = {},
  showCopyButton = true,
}: CodeBlockProps) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="relative">
      {showCopyButton && (
        <IconButton
          onClick={handleCopy}
          className={cn(
            'absolute top-2 right-2 p-2 rounded-md transition-colors',
            'bg-neutral-600 hover:bg-neutral-500 text-neutral-0 hover:text-neutral-0',
          )}
          aria-label={copied ? 'Copied!' : 'Copy code'}
        >
          {copied ? <Check size={16} weight="bold" /> : <Copy size={16} />}
        </IconButton>
      )}
      <SyntaxHighlighter
        language={language}
        style={vscDarkPlus}
        showLineNumbers={showLineNumbers}
        wrapLines={wrapLongLines}
        wrapLongLines={wrapLongLines}
        customStyle={{
          margin: 0,
          borderRadius: '0.375rem',
          fontSize: '0.875rem',
          ...customStyle,
        }}
      >
        {text}
      </SyntaxHighlighter>
    </div>
  );
};
